"use client";
/**
 * Media-led: the dish photograph carries the card.
 *
 * This is the composition for the endpoint that publishes pictures. All four
 * captured recipe assets are 300-pixel proxies at ratios from 1.000 to 1.508,
 * so the frame is a fixed photographic shape and the asset is cropped into it —
 * a plated dish is a scene rather than a cut-out, and cropping a scene is the
 * ordinary presentation.
 *
 * A food renders here too, and renders as the designed absence: the food
 * endpoint publishes no image field at all, and the fallback names the kind
 * instead of apologising for a picture that was never promised.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardProse,
  CresultcardStack,
  CRC_TILE_INSET,
  CresultcardAlign,
  CresultcardDensity,
  CresultcardFigureScale,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardProseMode,
  CresultcardStackDensity,
  CresultcardTextStep,
  CresultcardTilePadding,
  CresultcardTitleLines,
  useCresultcardLightbox,
} from "@/components/ui/Cresultcard";
import {
  C_NUTRITION_CARD_MODIFIER,
  C_NUTRITION_DETAIL_MEDIA_WIDTH,
} from "./cnutritioncard.constants";
import {
  CnutritioncardCategoryChips,
  CnutritioncardEnergy,
  CnutritioncardMetaLine,
  CnutritioncardShot,
} from "./CnutritioncardMeta";
import { CnutritioncardShell } from "./CnutritioncardShell";
import {
  CnutritioncardVariant,
  type CnutritioncardVariantProps,
} from "./cnutritioncard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Regular];
const CARD_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CnutritioncardPlateCard({
  data,
  servingScope,
  openControl,
}: CnutritioncardVariantProps): ReactElement {
  const { open, viewer } = useCresultcardLightbox({ assets: data.images });

  return (
    <>
      <CnutritioncardShell
        density={CresultcardDensity.Card}
        head={
          <span
            // The cap is the measured 300px long edge of every captured asset:
            // wider than that and `cover` would be upscaling a proxy.
            style={{ display: "block", width: C_NUTRITION_DETAIL_MEDIA_WIDTH }}
          >
            <CnutritioncardShot
              data={data}
              onOpen={() => open(0)}
              scale={CresultcardMediaScale.Face}
            />
          </span>
        }
        modifier={C_NUTRITION_CARD_MODIFIER[CnutritioncardVariant.PlateCard]}
        openable
        variant={CnutritioncardVariant.PlateCard}
        body={
          <div
            style={{ minWidth: 0, padding: CARD_INSET }}
          >
            <CresultcardStack
              density={CresultcardStackDensity.Snug}
            >
              <CresultcardIdentity
                openControl={openControl}
                secondary={
                  <CnutritioncardMetaLine
                    data={data}
                  />
                }
                title={data.name}
                titleLines={CresultcardTitleLines.Two}
                truncationOrder={CARD_TRUNCATION}
              />
              {/* The energy figure sits in the BODY, not in the identity's
                  trailing slot, and the reason is the scope line under it.
                  The basis can be the vendor's own
                  `1 oz, with bone cooked (yield after bone removed)` — 53
                  characters with its prefix — and a trailing slot beside a
                  title is the one place on a card that cannot absorb that
                  without taking the width from the name. In the body it gets
                  the card's whole inner measure. `Cshoppingcard` puts its
                  scope-bearing price in exactly the same region. */}
              <CnutritioncardEnergy
                align={CresultcardAlign.Start}
                data={data}
                scale={CresultcardFigureScale.Card}
                servingScope={servingScope}
              />
              {data.description != null && (
                <CresultcardProse
                  displayStep={CresultcardTextStep.Body}
                  mode={CresultcardProseMode.Display}
                  text={data.description}
                />
              )}
              <CnutritioncardCategoryChips
                data={data}
              />
            </CresultcardStack>
          </div>
        }
      />
      {viewer}
    </>
  );
}
